document.addEventListener('DOMContentLoaded', function () {
  // Mobile "curtain" gallery: the active photo's box clips itself away (following the
  // finger, then finishing the reveal) via clip-path; the photo itself never moves — the
  // target photo is already in place underneath, fixed, and stays still throughout.
  // Desktop keeps its own vertical scroll + thumbnails, untouched.
  function initMobileFeaturedCurtain() {
    const track = document.querySelector('[data-featured-track]');
    const currentEl = document.querySelector('[data-gallery-current]');
    const totalEl = document.querySelector('[data-gallery-total]');
    const featuredSection = document.querySelector('.brn-product__featured');

    if (!track) return;

    const slides = Array.from(track.querySelectorAll('.brn-product__featured__picture'));
    if (slides.length < 2) return;

    const DRAG_THRESHOLD_RATIO = 0.15;
    const FLICK_VELOCITY_PX_MS = 0.5;
    const DIRECTION_LOCK_DISTANCE = 10;
    // The prototype doesn't let the finger pull the photo all the way across: dragging
    // only ever shows a small peek of the next/previous photo as a direction hint. The
    // full reveal always plays out on release, via the committed animation below.
    const MAX_PEEK_RATIO = 0.25;
    const TRANSITION_MS = 400;
    const TRANSITION = `clip-path ${TRANSITION_MS}ms linear`;
    const OPEN_CLIP = 'inset(0 0 0 0)';
    const COUNTER_ROLL_MS = 220;

    let activeIndex = 0;
    let dragging = false;
    let hasDragged = false;
    // True once the current gesture has already triggered a slide change (mid-drag,
    // on crossing the distance threshold) — ignore the rest of that gesture's moves.
    let committedThisGesture = false;
    // null until the gesture moves enough to tell horizontal from vertical intent.
    let isHorizontalDrag = null;
    let startX = 0;
    let startY = 0;
    let deltaX = 0;
    let trackWidth = track.clientWidth;
    // Tracked on every move so a fast short flick can commit even under the distance threshold.
    let lastMoveX = 0;
    let lastMoveTime = 0;
    let velocity = 0;

    // Counter "roll" animation state — the digit span currently on screen, and its
    // in-flight animations (if any), so a fast series of swipes can cancel cleanly.
    let counterDigit = null;
    let counterOutAnim = null;
    let counterInAnim = null;

    function isMobile() {
      return window.innerWidth < 1024;
    }

    function getVideo(slide) {
      return slide.querySelector('video');
    }

    function playVideo(slide) {
      const video = getVideo(slide);
      if (!video) return;
      video.muted = true;
      video.playsInline = true;
      const playPromise = video.play();
      if (playPromise && playPromise.catch) playPromise.catch(() => { });
    }

    function pauseVideo(slide) {
      const video = getVideo(slide);
      if (video) video.pause();
    }

    function setActiveStacking() {
      slides.forEach((slide, i) => {
        slide.style.zIndex = i === activeIndex ? '3' : '1';
      });
    }

    // All inactive slides share z-index 1, so without this the browser falls back to DOM
    // order and whichever slide is last in the markup shows through — not necessarily the
    // one the drag is actually revealing. Lift just the real target above that baseline.
    function setTargetStacking(targetIndex) {
      slides.forEach((slide, i) => {
        if (i === activeIndex) return;
        slide.style.zIndex = i === targetIndex ? '2' : '1';
      });
    }

    // direction 1 (next) clips the slide away from its right edge; direction -1 (prev)
    // clips it away from its left edge — the same edge a positive/negative drag peeks from.
    function clipForDirection(direction) {
      return direction === 1 ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)';
    }

    function clipForDelta(deltaXPx) {
      if (!deltaXPx) return OPEN_CLIP;
      const pct = Math.min(100, (Math.abs(deltaXPx) / trackWidth) * 100);
      return deltaXPx < 0 ? `inset(0 ${pct}% 0 0)` : `inset(0 0 0 ${pct}%)`;
    }

    // Lazily wraps the counter's text in its own span so it can be animated independently
    // of the static "/" and total — the markup itself stays untouched.
    function initCounterDigit() {
      if (!currentEl || counterDigit) return;
      // The digit span is positioned absolutely (so outgoing/incoming can stack while
      // rolling), which takes it out of flow and stops it sizing its parent — without an
      // explicit width, `currentEl` would collapse to 0 and its `overflow: hidden` would
      // clip the number away entirely. The total is always at least as wide (same digits
      // or more), so borrow its rendered width as a stable size.
      if (totalEl) currentEl.style.width = `${totalEl.offsetWidth}px`;
      counterDigit = document.createElement('span');
      counterDigit.className = 'brn-product__featured__counter-digit';
      counterDigit.textContent = currentEl.textContent;
      currentEl.textContent = '';
      currentEl.appendChild(counterDigit);
    }

    // Rolls the counter from its current digit to the new one, top-to-bottom, matching
    // the Figma spec: the outgoing number rises and exits above, the incoming one rises
    // in from below.
    function updateCounter() {
      if (!currentEl) return;
      initCounterDigit();

      const nextValue = String(activeIndex + 1);
      if (counterDigit.textContent === nextValue) return;

      counterOutAnim?.cancel();
      counterInAnim?.cancel();

      const outgoing = counterDigit;
      const incoming = outgoing.cloneNode(false);
      incoming.textContent = nextValue;
      outgoing.insertAdjacentElement('afterend', incoming);
      counterDigit = incoming;

      counterOutAnim = outgoing.animate(
        [{ transform: 'translateY(0)' }, { transform: 'translateY(-100%)' }],
        { duration: COUNTER_ROLL_MS, easing: 'ease-in-out', fill: 'forwards' }
      );
      counterInAnim = incoming.animate(
        [{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }],
        { duration: COUNTER_ROLL_MS, easing: 'ease-in-out', fill: 'forwards' }
      );
      counterOutAnim.finished.then(() => outgoing.remove()).catch(() => { });
    }

    function resetInlineStyles() {
      slides.forEach((slide) => {
        slide.style.transition = '';
        slide.style.clipPath = '';
        slide.style.zIndex = '';
        slide.classList.remove('is-curtain-active');
      });
    }

    // Animates the current active slide's clip-path to `targetClip`, then runs `onDone`.
    // Falls back to a timeout in case transitionend never fires — e.g. bouncing back to a
    // clip-path value the slide already had, which the browser won't animate at all.
    function transitionTo(targetClip, onDone) {
      const activeSlide = slides[activeIndex];
      activeSlide.style.transition = TRANSITION;
      // Force the browser to register "transition: none → TRANSITION" before the next
      // clip-path change, otherwise both style changes can get batched into one paint
      // and the slide jumps straight to its end position instead of animating there.
      void activeSlide.offsetWidth;
      activeSlide.style.clipPath = targetClip;

      let done = false;
      function finish() {
        if (done) return;
        done = true;
        activeSlide.removeEventListener('transitionend', finish);
        clearTimeout(fallbackTimer);
        onDone(activeSlide);
      }
      const fallbackTimer = setTimeout(finish, TRANSITION_MS + 50);
      activeSlide.addEventListener('transitionend', finish, { once: true });
    }

    // Clips the active slide's box fully away in the drag's direction, revealing the
    // target slide — already in place, unclipped, underneath — then swaps the index.
    function commitTo(direction, targetIndex) {
      transitionTo(clipForDirection(direction), (activeSlide) => {
        activeSlide.style.transition = '';
        activeSlide.style.clipPath = '';
        activeSlide.classList.remove('is-curtain-active');
        pauseVideo(activeSlide);
        activeIndex = targetIndex;
        setActiveStacking();
        updateCounter();
        playVideo(slides[activeIndex]);
      });
    }

    function bounceBack() {
      transitionTo(OPEN_CLIP, (activeSlide) => {
        activeSlide.style.transition = '';
        activeSlide.style.clipPath = '';
        activeSlide.classList.remove('is-curtain-active');
        setActiveStacking();
      });
    }

    track.addEventListener('pointerdown', (event) => {
      if (!isMobile()) return;
      dragging = true;
      hasDragged = false;
      committedThisGesture = false;
      isHorizontalDrag = null;
      startX = event.clientX;
      startY = event.clientY;
      deltaX = 0;
      trackWidth = track.clientWidth;
      lastMoveX = event.clientX;
      lastMoveTime = event.timeStamp;
      velocity = 0;
      slides[activeIndex].style.transition = '';
      // Pointer capture is requested once we confirm this is a horizontal drag (below) —
      // not here, or a vertical page-scroll attempt would get hijacked from the start.
    });

    track.addEventListener('pointermove', (event) => {
      if (!dragging) return;

      const moveX = event.clientX - startX;
      const moveY = event.clientY - startY;

      if (isHorizontalDrag === null) {
        if (Math.abs(moveX) < DIRECTION_LOCK_DISTANCE && Math.abs(moveY) < DIRECTION_LOCK_DISTANCE) {
          return; // Not enough movement yet to tell direction.
        }
        isHorizontalDrag = Math.abs(moveX) > Math.abs(moveY);
        if (isHorizontalDrag) {
          track.setPointerCapture(event.pointerId);
          slides[activeIndex].classList.add('is-curtain-active');
        } else {
          // Vertical intent: hand the gesture back to the page (normal scroll) for good.
          dragging = false;
          return;
        }
      }

      // Claim the gesture so the browser doesn't treat it as its own swipe navigation.
      event.preventDefault();

      deltaX = moveX;
      hasDragged = true;

      const elapsed = event.timeStamp - lastMoveTime;
      if (elapsed > 0) velocity = (event.clientX - lastMoveX) / elapsed;
      lastMoveX = event.clientX;
      lastMoveTime = event.timeStamp;

      const direction = deltaX < 0 ? 1 : deltaX > 0 ? -1 : 0;
      const targetIndex = activeIndex + direction;
      // No slide in that direction: don't let the active photo move past its edge.
      if (direction !== 0 && (targetIndex < 0 || targetIndex >= slides.length)) {
        deltaX = 0;
      }

      // Crossing the distance threshold commits immediately, mid-drag — the finger only
      // ever gets a small peek (clamped below); it never pulls the photo all the way
      // across, and the swap doesn't wait for the finger to lift.
      const passedDistance = direction !== 0 && Math.abs(deltaX) > trackWidth * DRAG_THRESHOLD_RATIO
        && targetIndex >= 0 && targetIndex < slides.length;
      if (passedDistance) {
        committedThisGesture = true;
        dragging = false;
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        commitTo(direction, targetIndex);
        return;
      }

      setTargetStacking(targetIndex);
      // Clamp only what's drawn — deltaX itself stays the real drag distance, since the
      // threshold check above needs it unclamped (peek-clamping it would make committing
      // trivially easy).
      const maxPeek = trackWidth * MAX_PEEK_RATIO;
      const visualDeltaX = Math.max(-maxPeek, Math.min(maxPeek, deltaX));
      slides[activeIndex].style.clipPath = clipForDelta(visualDeltaX);
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;

      // No horizontal movement ever happened (tap, or a vertical scroll already handed
      // back), or the gesture already committed mid-drag above.
      if (!hasDragged || committedThisGesture) {
        deltaX = 0;
        return;
      }

      const direction = deltaX < 0 ? 1 : deltaX > 0 ? -1 : 0;
      const targetIndex = activeIndex + direction;
      // Didn't cross the distance threshold, but release as a fast short flick still
      // commits — matches native swipe feel instead of requiring a long, slow drag.
      const isFlick = Math.abs(velocity) > FLICK_VELOCITY_PX_MS && Math.sign(velocity) === -direction;
      const committed = direction !== 0 && isFlick && targetIndex >= 0 && targetIndex < slides.length;

      if (committed) {
        commitTo(direction, targetIndex);
      } else {
        bounceBack();
      }

      deltaX = 0;
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // Pause every video when the gallery scrolls out of the page viewport (e.g. user
    // keeps scrolling down the page), resume the active one when it comes back.
    if (featuredSection) {
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!isMobile()) return;
          if (entry.isIntersecting) {
            playVideo(slides[activeIndex]);
          } else {
            slides.forEach((slide) => pauseVideo(slide));
          }
        });
      }, { threshold: 0.1 });
      sectionObserver.observe(featuredSection);
    }

    window.addEventListener('resize', () => {
      trackWidth = track.clientWidth;
      if (!isMobile()) {
        resetInlineStyles();
        slides.forEach((slide) => pauseVideo(slide));
      } else {
        // Coming back from desktop: resetInlineStyles() above wiped every z-index to
        // '' (tied), so without this the browser falls back to DOM order and the last
        // slide paints on top instead of the actual active one.
        setActiveStacking();
        playVideo(slides[activeIndex]);
      }
    });

    // Only the mobile curtain needs slides layered by inline z-index (they're stacked
    // absolutely). On desktop the picture is in normal flow, so leaving this unguarded
    // put the active slide's z-index above the thumbnails rail's, hiding it whenever the
    // page loaded straight into desktop width (no resize event to trigger the cleanup).
    if (isMobile()) {
      setActiveStacking();
      playVideo(slides[activeIndex]);
    }
    updateCounter();
  }

  initMobileFeaturedCurtain();

  // Desktop gallery: sync thumbnail rail with the visible image, play/pause any video slide
  function initFeaturedGalleryDesktop() {
    const track = document.querySelector('[data-featured-track]');
    const thumbsRail = document.querySelector('[data-featured-thumbs]');

    if (!track || !thumbsRail) return;

    const images = Array.from(track.querySelectorAll('.brn-product__featured__image'));
    const thumbs = Array.from(thumbsRail.querySelectorAll('.brn-product__featured__thumb'));

    if (!images.length || !thumbs.length) return;

    function isMobile() {
      return window.innerWidth < 1024;
    }

    function setActiveThumb(index) {
      thumbs.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    function playIfVideo(el) {
      if (isMobile() || el.tagName !== 'VIDEO') return;
      el.muted = true;
      el.playsInline = true;
      const playPromise = el.play();
      if (playPromise && playPromise.catch) playPromise.catch(() => { });
    }

    function pauseIfVideo(el) {
      if (el.tagName === 'VIDEO') el.pause();
    }

    // Centers the active thumb in the rail's visible window (clamped at the
    // first/last thumbs) so it keeps following the image as the track scrolls.
    // Measured fresh every call rather than cached: this only runs from the
    // debounced timeout below (never from the hot scroll path), so the read
    // doesn't cause jank, and a stale cached maxScroll would permanently clamp
    // the last thumb short of fully scrolling into view.
    function centerActiveThumb(index) {
      const thumb = thumbs[index];
      const railHeight = thumbsRail.clientHeight;
      const maxScroll = thumbsRail.scrollHeight - railHeight;
      const desiredScrollTop = thumb.offsetTop + thumb.offsetHeight / 2 - railHeight / 2;

      thumbsRail.scrollTo({
        top: Math.max(0, Math.min(desiredScrollTop, maxScroll)),
        behavior: 'smooth',
      });
    }

    // Debounced so a fast scroll through several images doesn't restart the rail's
    // smooth-scroll animation on every one it passes — it only settles once the
    // track itself pauses on the image that ends up active.
    let centerTimeout = null;
    function scheduleCenterActiveThumb(index) {
      clearTimeout(centerTimeout);
      centerTimeout = setTimeout(() => centerActiveThumb(index), 120);
    }

    let activeIndex = -1;
    // Set while a thumb click's scroll is in flight, so the observer below doesn't
    // re-highlight (and re-center the rail on) every slide the track passes on its way.
    let clickScrollInFlight = false;
    let cancelClickScroll = null;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = images.indexOf(entry.target);
          if (!clickScrollInFlight && index !== -1 && index !== activeIndex) {
            activeIndex = index;
            setActiveThumb(index);
            if (!isMobile()) scheduleCenterActiveThumb(index);
          }
          playIfVideo(entry.target);
        } else {
          pauseIfVideo(entry.target);
        }
      });
    }, { root: track, threshold: 0.6 });

    images.forEach((image) => observer.observe(image));

    // Calls `onSettled` once the track stops emitting scroll events (or after
    // START_TIMEOUT_MS if it never scrolls at all, e.g. the target is already in place).
    // The idle timer only drops to IDLE_MS after the FIRST scroll event: Safari starts a
    // programmatic smooth scroll a frame or more after the scrollTo() call, so arming the
    // short timer upfront could settle before the scroll had even begun — handing the
    // track back to the IntersectionObserver mid-flight. Returns a canceller.
    function whenTrackSettles(onSettled) {
      const IDLE_MS = 150;
      const START_TIMEOUT_MS = 500;
      let idleTimer = setTimeout(finish, START_TIMEOUT_MS);
      function onScroll() {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(finish, IDLE_MS);
      }
      function cleanup() {
        clearTimeout(idleTimer);
        track.removeEventListener('scroll', onScroll);
      }
      function finish() {
        cleanup();
        onSettled();
      }
      track.addEventListener('scroll', onScroll, { passive: true });
      return cleanup;
    }

    // Scroll the track itself, not the page: scrollIntoView() would walk up and scroll
    // every scrollable ancestor, including the document behind the sticky wrapper.
    // Snapping is suspended for the duration: Safari lets the track's scroll-snap fight a
    // programmatic smooth scroll and pull it back to the previously snapped photo.
    function scrollToIndex(index) {
      const target = images[index].closest('.brn-product__featured__picture') || images[index];
      const trackRect = track.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      // Never infer the axis from a bare `scrollWidth > clientWidth`: the desktop track is
      // a column scroller whose width is fractional (542fr of `373fr 542fr 485fr`), and
      // Safari rounds scrollWidth up while clientWidth rounds down, so it reported a
      // phantom 1px of horizontal overflow. That sent every thumbnail click down the
      // horizontal branch, which scrolled nothing and left the photo unchanged. Require a
      // real horizontal range, and only when there is no vertical one to scroll instead.
      const SCROLL_EPSILON = 2;
      const isHorizontal =
        track.scrollWidth - track.clientWidth > SCROLL_EPSILON &&
        track.scrollHeight - track.clientHeight <= SCROLL_EPSILON;

      if (cancelClickScroll) cancelClickScroll();
      clearTimeout(centerTimeout);
      clickScrollInFlight = true;
      track.style.scrollSnapType = 'none';
      cancelClickScroll = whenTrackSettles(() => {
        track.style.scrollSnapType = '';
        clickScrollInFlight = false;
        cancelClickScroll = null;
        activeIndex = index;
        setActiveThumb(index);
        centerActiveThumb(index);
      });

      if (isHorizontal) {
        track.scrollTo({
          left: track.scrollLeft + (targetRect.left - trackRect.left),
          behavior: 'smooth',
        });
      } else {
        track.scrollTo({
          top: track.scrollTop + (targetRect.top - trackRect.top),
          behavior: 'smooth',
        });
      }
    }

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        scrollToIndex(index);
        setActiveThumb(index);
      });
    });
  }

  initFeaturedGalleryDesktop();

  // =====================
  // Zoom Overlay (Desktop & Mobile)
  // =====================
  function initGalleryZoom() {
    const overlay = document.querySelector('[data-zoom-overlay]');
    if (!overlay) return;

    const inner = overlay.querySelector('[data-zoom-inner]');
    const closeBtn = overlay.querySelector('[data-zoom-close]');
    const prevBtn = overlay.querySelector('[data-zoom-prev]');
    const nextBtn = overlay.querySelector('[data-zoom-next]');
    const stage = overlay.querySelector('[data-zoom-stage]');
    const swiperEl = overlay.querySelector('[data-zoom-swiper]');
    const slidesWrapper = overlay.querySelector('[data-zoom-slides]');
    // Video slides are excluded — zoom only supports images for now.
    const zoomImages = Array.from(document.querySelectorAll('.brn-product__featured__image'))
      .filter((el) => el.tagName === 'IMG');

    let isOpen = false;
    let isMobile = window.innerWidth < 1024;
    let swiperInstance = null;
    let desktopImg = null;
    let maxPanY = 0;

    function lockScroll() {
      document.body.classList.add('is--zoom-open');
    }
    function unlockScroll() {
      document.body.classList.remove('is--zoom-open');
    }

    function showOverlay() {
      overlay.hidden = false;
      overlay.setAttribute('aria-hidden', 'false');
      isOpen = true;
      lockScroll();
    }

    function hideOverlay() {
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
      isOpen = false;
      unlockScroll();
      // Cleanup desktop image
      if (desktopImg) {
        desktopImg.remove();
        desktopImg = null;
      }
      maxPanY = 0;
      // Cleanup swiper
      if (swiperInstance && typeof swiperInstance.destroy === 'function') {
        swiperInstance.destroy(true, true);
        swiperInstance = null;
      }
      if (slidesWrapper) slidesWrapper.innerHTML = '';
      // Restore ARIA
      stage && stage.setAttribute('aria-hidden', 'true');
      swiperEl && swiperEl.setAttribute('aria-hidden', 'true');
    }

    function onEsc(e) {
      if (e.key === 'Escape' && isOpen) {
        hideOverlay();
      }
    }

    let currentDesktopIndex = 0;
    let handleImageClick = null;
    let handleMouseMove = null;
    let handleKeyNav = null;

    function loadDesktopImage(index) {
      if (!stage || index < 0 || index >= zoomImages.length) return;

      currentDesktopIndex = index;
      const targetImg = zoomImages[index];
      const lowSrc = targetImg.currentSrc || targetImg.src;
      const highSrc = targetImg.dataset.zoomSrc;

      // Clean up old image
      if (desktopImg) {
        desktopImg.remove();
      }

      desktopImg = new Image();
      desktopImg.className = 'brn-product__zoom__image';
      desktopImg.src = lowSrc;

      desktopImg.onload = function () {
        const naturalH = desktopImg.naturalHeight;
        const naturalW = desktopImg.naturalWidth;
        const viewportH = window.innerHeight;
        const scaledH = naturalH * (window.innerWidth / naturalW);
        maxPanY = Math.max(0, Math.round(scaledH - viewportH));

        if (highSrc) {
          const hiRes = new Image();
          hiRes.onload = function () {
            desktopImg.src = highSrc;
            const scaledHHi = hiRes.naturalHeight * (window.innerWidth / hiRes.naturalWidth);
            maxPanY = Math.max(0, Math.round(scaledHHi - viewportH));
          };
          hiRes.src = highSrc;
        }
      };

      stage.innerHTML = '';
      stage.appendChild(desktopImg);

      // Re-attach click handler
      if (handleImageClick) {
        desktopImg.addEventListener('click', handleImageClick);
      }
    }

    function navigateToPrev() {
      const prevIndex = (currentDesktopIndex - 1 + zoomImages.length) % zoomImages.length;
      loadDesktopImage(prevIndex);
    }

    function navigateToNext() {
      const nextIndex = (currentDesktopIndex + 1) % zoomImages.length;
      loadDesktopImage(nextIndex);
    }

    function openDesktopZoom(targetImg) {
      if (!stage || !inner) return;

      // Find index of clicked image
      currentDesktopIndex = zoomImages.indexOf(targetImg);
      if (currentDesktopIndex === -1) currentDesktopIndex = 0;

      // Prepare stage
      stage.setAttribute('aria-hidden', 'false');
      swiperEl && swiperEl.setAttribute('aria-hidden', 'true');

      // Show navigation arrows
      if (prevBtn) prevBtn.hidden = false;
      if (nextBtn) nextBtn.hidden = false;

      showOverlay();
      loadDesktopImage(currentDesktopIndex);

      // Don't focus close button to avoid outline

      // Click on image to close zoom (desktop only)
      handleImageClick = function (e) {
        e.stopPropagation();
        hideOverlay();
      };
      stage.addEventListener('click', handleImageClick);

      // Mouse move maps Y to translateY
      handleMouseMove = function (e) {
        if (!desktopImg) return;
        if (maxPanY <= 0) {
          desktopImg.style.transform = 'translate3d(0,0,0)';
          return;
        }
        const ratioY = e.clientY / window.innerHeight;
        const offsetY = Math.round(ratioY * maxPanY);
        desktopImg.style.transform = `translate3d(0, ${-offsetY}px, 0)`;
      };
      inner.addEventListener('mousemove', handleMouseMove);

      // Keyboard navigation
      handleKeyNav = function (e) {
        if (!isOpen) return;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          navigateToPrev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          navigateToNext();
        }
      };
      document.addEventListener('keydown', handleKeyNav);

      // Button navigation
      if (prevBtn) {
        prevBtn.addEventListener('click', navigateToPrev);
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', navigateToNext);
      }

      // Remove event listeners on hide
      const prevHide = hideOverlay;
      hideOverlay = function () {
        inner.removeEventListener('mousemove', handleMouseMove);
        stage && stage.removeEventListener('click', handleImageClick);
        document.removeEventListener('keydown', handleKeyNav);
        if (prevBtn) {
          prevBtn.removeEventListener('click', navigateToPrev);
          prevBtn.hidden = true;
        }
        if (nextBtn) {
          nextBtn.removeEventListener('click', navigateToNext);
          nextBtn.hidden = true;
        }
        handleImageClick = null;
        handleMouseMove = null;
        handleKeyNav = null;
        prevHide();
        hideOverlay = prevHide;
      };
    }

    function buildSlides() {
      if (!slidesWrapper) return;
      slidesWrapper.innerHTML = '';
      zoomImages.forEach((imgEl) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        const slideImg = new Image();
        slideImg.className = 'brn-product__zoom__image';
        const lowSrc = imgEl.currentSrc || imgEl.src;
        const highSrc = imgEl.dataset.zoomSrc;
        slideImg.src = lowSrc;
        if (highSrc) {
          const hiRes = new Image();
          hiRes.onload = function () { slideImg.src = highSrc; };
          hiRes.src = highSrc;
        }
        slide.appendChild(slideImg);
        slidesWrapper.appendChild(slide);
      });
    }

    function openMobileZoom(startIndex) {
      if (!swiperEl) return;
      stage && stage.setAttribute('aria-hidden', 'true');
      swiperEl.setAttribute('aria-hidden', 'false');

      buildSlides(startIndex);
      showOverlay();
      closeBtn && closeBtn.focus({ preventScroll: true });

      // Initialize Swiper if available
      if (window.Swiper) {
        swiperInstance = new window.Swiper(swiperEl, {
          loop: true,
          initialSlide: startIndex || 0,
          slidesPerView: 1,
          spaceBetween: 0,
          speed: 300,
        });
      }
    }

    // Close interactions
    closeBtn && closeBtn.addEventListener('click', hideOverlay);
    document.addEventListener('keydown', onEsc);

    // Image interactions
    zoomImages.forEach((imgEl, index) => {
      imgEl.addEventListener('click', function () {
        isMobile = window.innerWidth < 1024;
        if (isMobile) {
          openMobileZoom(index);
        } else {
          openDesktopZoom(imgEl);
        }
      }, { passive: true });
    });

    // Public API (optional)
    window.initGalleryZoom = initGalleryZoom;
  }

  // Initialize zoom
  initGalleryZoom();
});
